/**
 * SRP stage labels from the API's glossary (`GET /srp/glossary`, HANDOFF §0.1: never hard-code SRP ids, labels or
 * limits). The glossary changes with an SRP release only, so it is cached for the session.
 *
 * The glossary is English. In another language the four stages read in the page's language (glossary.pl.md:
 * stage names are translated, SRP field labels are not): `misc.stage_<id>`. In English the glossary's label wins
 * (the SRP's own words); a known stage still has a name before the glossary loads or when it fails.
 */
import { useTranslation } from "react-i18next";
import { useGetSrpGlossary } from "@brevalt/api-client/customer";

interface GlossaryStages {
  stages?: { id?: unknown; label?: unknown }[];
}

/** The stages the app names in the page's language (the API's `StageId`). */
const STAGE_KEYS = {
  early_warning: "misc.stage_early_warning",
  notification: "misc.stage_notification",
  intermediate: "misc.stage_intermediate",
  final: "misc.stage_final",
} as const;

/**
 * `label("early_warning")` → "Early warning" ("Wczesne ostrzeżenie" in Polish); another stage id: the glossary's
 * label once it is loaded, else undefined.
 */
export function useStageLabels(enabled = true): (stage: string | null | undefined) => string | undefined {
  const { t, i18n } = useTranslation();
  const glossary = useGetSrpGlossary({ query: { enabled, staleTime: Infinity, gcTime: Infinity } });
  const stages = (glossary.data as GlossaryStages | undefined)?.stages ?? [];
  const english = (i18n.resolvedLanguage ?? i18n.language).split("-")[0] === "en";
  return (stage) => {
    const label = stages.find((s) => s.id === stage)?.label;
    const fromGlossary = typeof label === "string" ? label : undefined;
    const key = stage && Object.hasOwn(STAGE_KEYS, stage) ? STAGE_KEYS[stage as keyof typeof STAGE_KEYS] : undefined;
    if (!key) return fromGlossary;
    return english ? (fromGlossary ?? t(key)) : t(key);
  };
}
